import React, {useCallback, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {formatDataIsoBR, formatReais} from "../../shared/utils/formatters";
import {COBRANCA_FILTROS, getMeioDaCobrancaLabel, hojeIso, MEIO_DA_COBRANCA_LABELS, situacaoDaCobranca} from "./cobrancasUtils";
import {useCobrancas} from "./useCobrancas";
import CobrancaDetailsDialog from "./components/CobrancaDetailsDialog";
import ReguaCard from "./components/ReguaCard";

const nomeDe = (empresa) => empresa?.nome_fantasia || empresa?.razao_social || empresa?.nome || '';

/*
 * Cobranças (BBF-158): a régua de inadimplência no alto e, embaixo, todas as cobranças, com o boleto e o Pix de cada
 * uma no detalhe. Cada fatura vira uma cobrança por meio de pagamento.
 */
export default function FinanceiroCobrancasPage({empresas = [], onAbrirFatura, aoMudar}) {
    const {regua, filtros, lista, mudarFiltro, recarregar} = useCobrancas();
    const [aberta, setAberta] = useState(null);
    const hoje = hojeIso();

    const nomes = useMemo(() => new Map(empresas.map((empresa) => [empresa.id, nomeDe(empresa)])), [empresas]);
    const nomeDaEmpresa = useCallback((id) => nomes.get(id) || '', [nomes]);
    const opcoesDeEmpresa = useMemo(
        () => [...empresas].filter((empresa) => empresa?.id).sort((a, b) => nomeDe(a).localeCompare(nomeDe(b), 'pt-BR')),
        [empresas]
    );

    const depoisDeMudar = useCallback(async () => {
        await recarregar();
        await aoMudar?.();
    }, [recarregar, aoMudar]);

    return (
        <Stack spacing={2.5}>
            <ReguaCard linhas={regua.linhas} carregando={regua.carregando} erro={regua.erro} onAbrirCobranca={setAberta} />

            <Paper variant="outlined" sx={{borderRadius: 2.5, overflow: 'hidden'}}>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} sx={{p: {xs: 2, md: 2.5}, borderBottom: 1, borderColor: 'divider'}}>
                    <TextField select size="small" label="Situação" value={filtros.filtro} onChange={(e) => mudarFiltro('filtro', e.target.value)} sx={{minWidth: 180}}>
                        {COBRANCA_FILTROS.map((opcao) => <MenuItem key={opcao.valor || 'todas'} value={opcao.valor}>{opcao.rotulo}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Meio" value={filtros.meio} onChange={(e) => mudarFiltro('meio', e.target.value)} sx={{minWidth: 160}}>
                        <MenuItem value="">Todos os meios</MenuItem>
                        {Object.entries(MEIO_DA_COBRANCA_LABELS).map(([valor, rotulo]) => <MenuItem key={valor} value={valor}>{rotulo}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Empresa" value={filtros.empresaId} onChange={(e) => mudarFiltro('empresaId', e.target.value)} sx={{minWidth: 220, flex: 1}}>
                        <MenuItem value="">Todas as empresas</MenuItem>
                        {opcoesDeEmpresa.map((empresa) => <MenuItem key={empresa.id} value={empresa.id}>{nomeDe(empresa) || empresa.id}</MenuItem>)}
                    </TextField>
                </Stack>

                {lista.erro ? <Alert severity="error" sx={{m: 2}}>{lista.erro}</Alert> : null}
                {!lista.erro && lista.carregando && !lista.itens.length ? (
                    <Typography color="text.secondary" sx={{p: 2.5}}>Carregando as cobranças...</Typography>
                ) : null}
                {!lista.erro && !lista.carregando && !lista.itens.length ? (
                    <Typography color="text.secondary" sx={{p: 2.5}}>Nenhuma cobrança com esses filtros.</Typography>
                ) : null}

                {!lista.erro && lista.itens.length ? (
                    <>
                        <Box sx={{display: {xs: 'none', md: 'block'}, overflowX: 'auto'}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Empresa</TableCell>
                                        <TableCell>Meio</TableCell>
                                        <TableCell>Vencimento</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                        <TableCell>Situação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lista.itens.map((cobranca) => {
                                        const situacao = situacaoDaCobranca(cobranca, hoje);
                                        return (
                                            <TableRow key={cobranca.id} hover sx={{cursor: 'pointer'}} onClick={() => setAberta(cobranca.id)}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>{nomeDaEmpresa(cobranca.empresaId) || cobranca.empresaId}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {cobranca.licencaIds?.length > 1 ? `${cobranca.licencaIds.length} assinaturas` : '1 assinatura'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{getMeioDaCobrancaLabel(cobranca.meio)}</TableCell>
                                                <TableCell sx={{whiteSpace: 'nowrap'}}>{formatDataIsoBR(cobranca.vencimento)}</TableCell>
                                                <TableCell align="right" sx={{whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'}}>{formatReais(cobranca.valor)}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" variant="outlined" label={situacao.rotulo} color={situacao.tom === 'default' ? undefined : situacao.tom} />
                                                    {cobranca.pagaEm ? (
                                                        <Typography variant="caption" display="block" color="text.secondary">em {formatDataIsoBR(cobranca.pagaEm)}</Typography>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                        <Stack sx={{display: {xs: 'flex', md: 'none'}}} divider={<Box sx={{borderTop: 1, borderColor: 'divider'}} />}>
                            {lista.itens.map((cobranca) => {
                                const situacao = situacaoDaCobranca(cobranca, hoje);
                                return (
                                    <Box key={cobranca.id} role="button" tabIndex={0} onClick={() => setAberta(cobranca.id)} sx={{p: 2, cursor: 'pointer'}}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                            <Typography variant="body2" fontWeight={600} sx={{minWidth: 0}}>{nomeDaEmpresa(cobranca.empresaId) || cobranca.empresaId}</Typography>
                                            <Chip size="small" variant="outlined" label={situacao.rotulo} color={situacao.tom === 'default' ? undefined : situacao.tom} />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {getMeioDaCobrancaLabel(cobranca.meio)} · {formatReais(cobranca.valor)} · vence {formatDataIsoBR(cobranca.vencimento)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                ) : null}

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider'}}>
                    <Typography variant="body2" color="text.secondary">
                        {lista.total ? `${lista.total} ${lista.total === 1 ? 'cobrança' : 'cobranças'}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton aria-label="Página anterior" disabled={filtros.pagina <= 1} onClick={() => mudarFiltro('pagina', filtros.pagina - 1)}>
                            <ChevronLeft size={18} />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">Página {filtros.pagina} de {Math.max(1, lista.paginas)}</Typography>
                        <IconButton aria-label="Próxima página" disabled={filtros.pagina >= lista.paginas} onClick={() => mudarFiltro('pagina', filtros.pagina + 1)}>
                            <ChevronRight size={18} />
                        </IconButton>
                    </Stack>
                </Stack>
            </Paper>

            {/* Uma instância por cobrança: o aviso de uma ação não passa para a próxima que for aberta. */}
            <CobrancaDetailsDialog
                key={aberta || 'nenhuma'}
                open={Boolean(aberta)}
                cobrancaId={aberta}
                nomeDaEmpresa={nomeDaEmpresa}
                onClose={() => setAberta(null)}
                onAbrirFatura={onAbrirFatura ? (faturaId) => { setAberta(null); onAbrirFatura(faturaId); } : undefined}
                aoMudar={depoisDeMudar}
            />
        </Stack>
    );
}
